import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const SearchableSelect = ({ 
  value, 
  onValueChange, 
  options = [], 
  placeholder = 'Sélectionner...', 
  searchPlaceholder = 'Rechercher...',
  className = '',
  renderOption,
  disabled = false,
  'data-testid': testId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  }, [open]);

  const filtered = search
    ? options.filter(opt => {
        const label = (opt.label || '').toLowerCase();
        const val = (opt.value || '').toLowerCase();
        const q = search.toLowerCase();
        return label.includes(q) || val.includes(q);
      })
    : options;

  const selectedOption = options.find(o => o.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between bg-zinc-950 border-zinc-800 text-white hover:bg-zinc-900 hover:text-white font-normal h-9 px-3',
            !value && 'text-zinc-500',
            className
          )}
          data-testid={testId}
        >
          <span className="truncate text-left flex-1 text-sm">{displayLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-zinc-900 border-zinc-800" align="start">
        <div className="flex items-center border-b border-zinc-800 px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 text-zinc-500" />
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex h-9 w-full bg-transparent py-3 text-sm text-white outline-none placeholder:text-zinc-500"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
        <div className="max-h-[220px] overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="py-4 text-center text-sm text-zinc-500">Aucun résultat</p>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={cn(
                  'relative flex w-full cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none transition-colors',
                  value === opt.value
                    ? 'bg-amber-500/10 text-amber-400'
                    : 'text-white hover:bg-zinc-800'
                )}
                onClick={() => {
                  onValueChange(opt.value === value ? '' : opt.value);
                  setOpen(false);
                }}
                data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
              >
                <Check className={cn('mr-2 h-3.5 w-3.5 flex-shrink-0', value === opt.value ? 'opacity-100 text-amber-400' : 'opacity-0')} />
                <span className="truncate">
                  {renderOption ? renderOption(opt) : opt.label}
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
